// D5 documents/context-files API — carved from the v1 hub (`scribeAPI.ts`)
// and the v2 hub (`scribeAPIV2.ts`) at ARCFE-990, over the E5 registry; both
// hubs re-export name-intact. The DOCUMENT MUTATIONS did not carve: they
// import `generateDocument` (REVIEW, the chat/AI-commands cluster, D2d) —
// the blocker E3 recorded — and stay app-side until that cluster resolves.
import { getScribeHttpClients } from '@heidi/api/client';
import { Brain, type ContentType, type components } from '@heidi/schema/ml-scribe';

import { logger } from '@heidi/runtime/logger';

import {
  type SessionContextDocuments,
  type UpdateXmlFieldsResponse,
  type XmlDocumentFieldsResponse,
  type XmlDocumentFormat,
  type XmlFieldUpdate,
} from '../../contracts';

type DocumentResponse = components['schemas']['DocumentView'];
type DocumentTabType = components['schemas']['DocumentTabType'];
type DocumentGenType = components['schemas']['DocumentGenType'];
type GenerationMethod = components['schemas']['GenerationMethod'];
type VoiceStyle = components['schemas']['VoiceStyle'];
type GenerateDocumentContentRequestV2 =
  components['schemas']['GenerateDocumentContentRequestV2'];
type ShareDocumentThroughEmailRequestV2 =
  components['schemas']['ShareDocumentThroughEmailRequestV2'];
type ShareDocumentResponseV2 = components['schemas']['ShareDocumentResponseV2'];
type UpdateDocumentsRequest = components['schemas']['UpdateDocumentListRequest'];
type UpdateDocumentsResponse = components['schemas']['UpdateDocumentListResponse'];
type UpdateSingleSessionContextDocument =
  components['schemas']['UpdateSingleSessionContextDocument'];
type UpdateSessionContextDocumentsRequest =
  components['schemas']['UpdateSessionContextDocumentsRequest'];
type DeleteSessionContextDocumentsRequest =
  components['schemas']['DeleteSessionContextDocumentsRequest'];
type DeleteSessionContextDocumentsResponse =
  components['schemas']['DeleteSessionContextDocumentsResponse'];

/** Hand-authored in `@/types`, moved verbatim (not a schema pick). */
export type UploadContextDocumentsMetaData = {
  name: string;
  date: string;
  additional_context: string;
  unstructured: boolean;
};

/** Hand-authored in the v2 hub, moved verbatim. */
export type ShareDocumentByEmailRecipientsRequest = {
  recipients: string[];
  content?: string | null;
};

type GetDocumentsResponse = components['schemas']['GetDocumentsResponse'];
type GetDocumentResponse = components['schemas']['DocumentView'];

/** @domain session */
export const getDocumentsBySessionId = (sessionId: string) => {
  return getScribeHttpClients().scribeHttp.get<GetDocumentsResponse>(
    `/sessions/${sessionId}/document/`
  );
};

/** @domain session */
export const getDocumentById = (sessionId: string, documentId: string) => {
  return getScribeHttpClients().scribeHttp.get<GetDocumentResponse>(
    `/sessions/${sessionId}/document/${documentId}`
  );
};

/** @domain session */
export const getContextDocuments = (sessionId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionContextDocuments[]>(
    `/sessions/${sessionId}/context-documents`
  );
};

// ---------------------------------------------------------------------------
// Document + context-document writes (ARCFE-1004)
//
// `contentType` is a HOISTED PARAMETER on the create call. The pre-move fn
// computed it from `isEnableBlockEditorV1(resolveActiveSessionProvider(id))`
// — an app flag composed with capture's provider resolution — and per the E3
// flag disposition the app passes the RESOLVED value in. Same treatment as
// `useMutateUpdateConsultNoteOptions` at ARCFE-991.
// ---------------------------------------------------------------------------

export const postCreateEmptyDocument = ({
  sessionId,
  templateId,
  aiCommandId,
  aiCommandText,
  generationMethod,
  // `Brain.LEFT` — the app's `DEFAULT_BRAIN`. Read in the DEFAULT PARAMETER
  // (evaluated at call time) rather than at module scope, so suites that mock
  // `@heidi/schema/ml-scribe` can still load this module.
  brain = Brain.LEFT,
  index,
  voiceStyle,
  documentTabType,
  isPersonalizationApplied,
  taskId,
  contentType,
}: {
  sessionId: string;
  documentTabType?: DocumentTabType | null;
  templateId?: string;
  aiCommandId?: string;
  aiCommandText?: string;
  generationMethod: GenerationMethod;
  brain?: Brain;
  index?: number;
  voiceStyle?: VoiceStyle;
  isPersonalizationApplied?: boolean;
  taskId?: string;
  /** Resolved app-side from the block-editor flag (E3 disposition). */
  contentType: ContentType;
}) => {
  return getScribeHttpClients().scribeHttpV2.post<DocumentResponse>(
    `/sessions/${sessionId}/documents`,
    {
      template_id: templateId,
      ai_command_id: aiCommandId,
      ai_command_text: aiCommandText,
      generation_method: generationMethod,
      brain,
      index,
      voice_style: voiceStyle,
      content_type: contentType,
      document_tab_type: documentTabType || null,
      is_personalization_applied: isPersonalizationApplied,
      task_id: taskId,
    } satisfies GenerateDocumentContentRequestV2
  );
};

export const shareDocumentByEmail = (
  sessionId: string,
  documentId: string,
  payload: ShareDocumentByEmailRecipientsRequest
) => {
  const body: ShareDocumentThroughEmailRequestV2 = {
    recipients: payload.recipients,
    ...(payload.content !== undefined ? { content: payload.content } : {}),
  };
  return getScribeHttpClients().scribeHttpV2.post<ShareDocumentResponseV2>(
    `/sessions/${sessionId}/documents/${documentId}/shares/email`,
    body
  );
};

export const updateDocuments = ({
  session_id,
  documents,
  find_and_replace,
  find_and_replace_ignore_case,
  find_and_replace_partial_match,
}: UpdateDocumentsRequest & { session_id: string }) => {
  return getScribeHttpClients().scribeHttpV2.patch<UpdateDocumentsResponse>(
    `/sessions/${session_id}/documents`,
    {
      documents,
      find_and_replace,
      find_and_replace_ignore_case,
      find_and_replace_partial_match,
    }
  );
};

export const uploadContextDocument = ({
  sessionId,
  file,
  metaData,
  onProgressUpdate,
  signal,
}: {
  sessionId: string;
  file: File;
  metaData: UploadContextDocumentsMetaData;
  onProgressUpdate?: (fileName: string, progress: number) => void;
  signal?: AbortSignal;
}) => {
  const formData = new FormData();

  // Append each file to the formData
  formData.append('context_documents', file); // The key must match the API's expected parameter name
  formData.append('meta_data', JSON.stringify(metaData));

  // Perform the POST request
  return getScribeHttpClients().scribeHttpV2.post(
    `/sessions/${sessionId}/context-documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data', // Ensures the correct content type
      },
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          logger.debug(`Upload progress: ${percentCompleted}% for ${file.name}`);
          if (onProgressUpdate) {
            onProgressUpdate(file.name, percentCompleted);
          }
        }
      },
    }
  );
};

/**
 * Fetch the original uploaded file for a context document.
 *
 * Responds 404 when no original was retained, which is the case for documents
 * uploaded before originals were kept.
 */
export const getContextDocumentFile = (sessionId: string, documentId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<Blob>(
    `/sessions/${sessionId}/context-documents/${documentId}/file`,
    { responseType: 'blob' }
  );
};

export const updateContextDocuments = ({
  sessionId,
  documents,
}: {
  sessionId: string;
  documents: UpdateSingleSessionContextDocument[];
}) => {
  const data: UpdateSessionContextDocumentsRequest = {
    context_documents: documents,
  };
  return getScribeHttpClients().scribeHttpV2.patch<SessionContextDocuments[]>(
    `/sessions/${sessionId}/context-documents`,
    data
  );
};

export const deleteContextDocuments = (sessionId: string, documentIds: string[]) => {
  const data: DeleteSessionContextDocumentsRequest = {
    ids: documentIds,
  };
  return getScribeHttpClients().scribeHttpV2.delete<DeleteSessionContextDocumentsResponse>(
    `/sessions/${sessionId}/context-documents`,
    { data }
  );
};

/** `PATCH /sessions/:id/document/:docId` (v1) — rename / content update. */
export const updateDocument = ({
  sessionId,
  documentId,
  name,
  content,
  generationType,
  updatedDictation,
  contentType,
}: {
  sessionId: string;
  contentType: ContentType;
  documentId?: string;
  name?: string;
  content?: string;
  generationType?: DocumentGenType;
  updatedDictation?: string;
}) => {
  return getScribeHttpClients().scribeHttp.patch<DocumentResponse>(
    `/sessions/${sessionId}/document/${documentId}`,
    {
      name,
      content,
      generation_type: generationType,
      updated_dictation: updatedDictation,
      content_type: contentType,
    }
  );
};

/** `DELETE /sessions/:id/document/:docId` (v1). */
export const deleteDocumentById = (sessionId: string, documentId: string) => {
  return getScribeHttpClients().scribeHttp.delete(
    `/sessions/${sessionId}/document/${documentId}`
  );
};

/**
 * `POST /sessions/:id/documents/pdf-document`.
 *
 * The pre-move fn opened with a transcript-deleted guard that reads the
 * session cache AND `areAllTranscriptsEmpty`, which lives in the
 * capture-boundary module this family excludes (ARCFE-1001). The guard
 * therefore stays app-side and is injected into the hook, which still runs it
 * before this call — same rejection, same sentinel, same ordering.
 */
export const createPDFDocument = (
  sessionId: string,
  createPDFDocumentRequest: GenerateDocumentContentRequestV2
) => {
  return getScribeHttpClients().scribeHttpV2.post<DocumentResponse>(
    `/sessions/${sessionId}/documents/pdf-document`,
    createPDFDocumentRequest
  );
};

const xmlDocumentBasePath = (format: XmlDocumentFormat) =>
  format === 'xfa_pdf' ? '/xfa-document' : '/xml-document';

/**
 * `GET /xml-document/:documentId/fields` — the LLM-filled values of an XML
 * form document. `format: 'xfa_pdf'` reads the same shape from
 * `/xfa-document`.
 *
 * **Answers 404 until the fill has completed.** A 404 here means "not ready",
 * not "not found", which is why `useQueryXmlDocumentFields` keeps polling
 * through it instead of surfacing an error.
 *
 * @domain session
 */
export const getXmlDocumentFields = (
  documentId: string,
  format: XmlDocumentFormat = 'xml'
) => {
  return getScribeHttpClients().scribeHttpV2.get<XmlDocumentFieldsResponse>(
    `${xmlDocumentBasePath(format)}/${documentId}/fields`
  );
};

/**
 * `PATCH /xml-document/:documentId/fields` — persist edited field values.
 *
 * Values are the source the XML/JSON downloads render from, so an edit needs
 * no artefact regeneration.
 *
 * @domain session
 */
export const updateXmlDocumentFields = (
  documentId: string,
  fields: XmlFieldUpdate[],
  format: XmlDocumentFormat = 'xml'
) => {
  return getScribeHttpClients().scribeHttpV2.patch<UpdateXmlFieldsResponse>(
    `${xmlDocumentBasePath(format)}/${documentId}/fields`,
    { fields }
  );
};

/**
 * `GET /xml-document/:documentId/download` — the filled XML itself.
 *
 * `responseType: 'blob'`: the body is `application/xml`, and letting axios
 * parse it would hand back a string the download path then has to re-wrap.
 *
 * @domain session
 */
export const downloadXmlDocument = (documentId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<Blob>(
    `/xml-document/${documentId}/download`,
    { responseType: 'blob' }
  );
};

/**
 * `GET /xfa-document/:documentId/download` — the original XFA PDF with the
 * filled values written into its datasets packet.
 *
 * @domain session
 */
export const downloadXfaDocumentPdf = (documentId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<Blob>(
    `/xfa-document/${documentId}/download`,
    { responseType: 'blob' }
  );
};

/**
 * `GET /json-document/:documentId/download` — the filled JSON itself.
 *
 * Flag-independent: a document filled while the flag was on stays
 * downloadable after the flag is turned off. 404 means the fill has not
 * landed yet. `responseType: 'blob'` because the body is `application/json`
 * and axios would otherwise hand back a parsed object.
 *
 * @domain session
 */
/**
 * `POST /sessions/{sessionId}/document/{documentId}/generate-insights`.
 * V1 client, boolean body, same path the hub used.
 * @domain session
 */
export const documentGenerateInsights = ({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId: string;
}) => {
  return getScribeHttpClients().scribeHttp.post<boolean>(
    `/sessions/${sessionId}/document/${documentId}/generate-insights`
  );
};

export const downloadJsonDocument = (documentId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<Blob>(
    `/json-document/${documentId}/download`,
    { responseType: 'blob' }
  );
};
